import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Panel } from "./OfficeLayout";
import { QrAsset } from "../../components/office/QrAsset";
import { api, errMsg, money } from "../../lib/api";

const TYPES = ["Flyer Campaign", "Direct Mail", "Postcard Campaign", "Door Hanger", "Business Card", "Letter", "SMS Campaign", "Email Campaign", "Social Media",
  "Community Event", "Seminar", "Church/Community Partnership", "Real-Estate Campaign", "ZIP-Code Campaign", "Neighborhood Campaign", "City Campaign", "Property-Specific Outreach", "Other"];
const CHANNELS = ["Direct Mail", "Hand Delivered", "SMS", "Email", "Social Media", "Digital Display", "Event", "Community Organization", "Affiliate", "Team Member"];
const EMPTY = { name: "", type: TYPES[0], city: "", zip: "", partner_id: "", team_member_id: "", channel: CHANNELS[0], flyer_version: "", start_date: "", end_date: "", notes: "", cost: "" };

const CampaignForm = ({ partners, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const go = async () => {
    if (!f.name) return toast.error("Campaign name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/office/campaigns", { ...f, cost: Number(f.cost || 0), partner_id: f.partner_id || null, team_member_id: f.team_member_id || null });
      toast.success(`Tracking link generated: ${data.link.code}`);
      setF(EMPTY); onCreated(data);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const sel = (k, opts, testId) => (
    <select data-testid={testId} value={f[k]} onChange={set(k)} className="h-9 rounded border border-border bg-background px-2 text-sm">{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
  );
  const affs = partners.filter((p) => p.type !== "team_member");
  const tms = partners.filter((p) => p.type === "team_member");
  return (
    <Panel title="Create campaign" testId="campaign-form">
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <Input data-testid="camp-name" placeholder="Campaign name" value={f.name} onChange={set("name")} className="h-9 xl:col-span-2" />
        {sel("type", TYPES.map((t) => [t, t]), "camp-type")}
        {sel("channel", CHANNELS.map((t) => [t, t]), "camp-channel")}
        <Input data-testid="camp-city" placeholder="City" value={f.city} onChange={set("city")} className="h-9" />
        <Input data-testid="camp-zip" placeholder="ZIP code" value={f.zip} onChange={set("zip")} className="h-9" />
        {sel("partner_id", [["", "Affiliate (none)"], ...affs.map((p) => [p.id, `${p.name} (${p.id})`])], "camp-affiliate")}
        {sel("team_member_id", [["", "Team member (none)"], ...tms.map((p) => [p.id, `${p.name} (${p.id})`])], "camp-team-member")}
        <Input data-testid="camp-version" placeholder="Flyer version (e.g. Version B)" value={f.flyer_version} onChange={set("flyer_version")} className="h-9" />
        <Input data-testid="camp-start" type="date" value={f.start_date} onChange={set("start_date")} className="h-9" />
        <Input data-testid="camp-end" type="date" value={f.end_date} onChange={set("end_date")} className="h-9" />
        <Input data-testid="camp-cost" placeholder="Marketing cost ($)" value={f.cost} onChange={set("cost")} className="h-9" />
        <Input data-testid="camp-notes" placeholder="Notes" value={f.notes} onChange={set("notes")} className="h-9 sm:col-span-2 xl:col-span-4" />
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">If both an affiliate and a team member are chosen, the affiliate owns attribution.</p>
      <button data-testid="generate-tracking-link-btn" disabled={busy} onClick={go} className="mt-3 h-9 rounded bg-primary px-4 text-sm font-bold text-primary-foreground">Generate tracking link</button>
    </Panel>
  );
};

export default function Campaigns() {
  const { user } = useOutletContext();
  const admin = user.role === "admin";
  const [camps, setCamps] = useState([]);
  const [partners, setPartners] = useState([]);
  const [links, setLinks] = useState([]);
  const load = () => {
    api.get("/office/campaigns").then(({ data }) => setCamps(data));
    api.get("/office/links").then(({ data }) => setLinks(data.filter((l) => !l.campaign_id)));
    if (admin) api.get("/office/partners").then(({ data }) => setPartners(data));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  const pname = (id) => partners.find((p) => p.id === id)?.name || id;
  return (
    <div className="space-y-4" data-testid="campaigns-page">
      <h1 className="text-2xl font-bold">Campaigns &amp; QR Codes</h1>
      {admin && <CampaignForm partners={partners} onCreated={load} />}
      <div className="grid gap-4 2xl:grid-cols-2">
        {camps.map((c) => (
          <Panel key={c.id} title={c.id} right={<span className="font-mono text-[10px] text-muted-foreground">{c.type} • {c.channel}</span>} testId={`campaign-${c.id}`}>
            <p className="mb-2 text-sm font-semibold">{c.name}</p>
            <p className="mb-3 text-xs text-muted-foreground">{c.city || "—"} {c.zip} • {c.flyer_version || "—"} • {c.start_date || "?"} → {c.end_date || "open"} • Cost {money(c.cost)} {c.partner_id && `• ${pname(c.partner_id)}`}</p>
            {c.link && <QrAsset link={c.link} campaign={c} />}
          </Panel>
        ))}
      </div>
      {!!links.length && (
        <Panel title="Master referral links">
          <div className="grid gap-3 xl:grid-cols-2">{links.map((l) => <QrAsset key={l.id} link={l} />)}</div>
        </Panel>
      )}
    </div>
  );
}
